import {
	Button,
	Form,
	Header,
	type Job,
	Message,
	Modal,
	TraitementLong,
} from '@mairistem/composants';
import {
	type Bordereau,
	type Exercice,
	type Mouvement,
	Sens,
} from '@mairistem/comptabilite';
import { Date, getErrorMessage } from '@mairistem/finances-utils';
import type { DropdownOption } from '@mairistem/liste';
import dayjs from 'dayjs';
import { isEmpty, isNil, map } from 'lodash';
import React from 'react';
import { toast } from 'react-toastify';
import CODE_BHM from '../../../../../constants/mouvements/codeBHM';
import DUPLICATION_ERRORS from '../../../../../errors/duplicationErrors';
import type { BordereauWithState } from '../../../../../interfaces/pieces/TreeRow';
import { dupliquerBordereau } from '../../../../../services/piecesComptables/bordereaux/bordereaux';
import getSeries from '../../../../../services/piecesComptables/series';
import BordereauForm from '../../../../Mouvement/FichePanel/InformationsGenerales/BordereauForm';
import BordereauSelect from '../BordereauSelect';

interface FormData {
	shouldReprendreReferences: boolean;
	isEcheance: boolean;
	dateEcheance?: string;
	isDepartDelaiGlobalPaiement: boolean;
	dateDepartDelaiGlobalPaiement?: string;
	isPrelevement: boolean;
	datePrelevement?: string;
	isRealisationPrestation: boolean;
	dateRealisationPrestationDebut?: string;
	dateRealisationPrestationFin?: string;
	bordereauIsModel?: boolean;
}

interface ChangeDataBordereau extends BordereauWithState, FormData {}
interface ChangeDataMouvement extends Mouvement, FormData {}

interface DuplicationModalProps {
	onValidate: (
		data: ChangeDataBordereau | ChangeDataMouvement,
	) => Promise<any> | Job;
	onClose: VoidFunction;
	visible: boolean;
	bordereau?: BordereauWithState;
	mouvement?: Mouvement;
	exercice?: Exercice;
	typePes?: string;
}

type CustomBordereauKeys =
	| 'sens'
	| 'typePes'
	| 'numero'
	| 'dateEmission'
	| 'identifiantSerie'
	| 'libelle';

const HORS_SERIE = {
	'data-testid': 'serieHorsSerie',
	key: 0,
	text: 'Hors-Série',
	value: 0,
} as const;

const DuplicationModal = ({
	onClose,
	onValidate,
	visible,
	bordereau,
	mouvement,
	exercice,
	typePes,
}: DuplicationModalProps) => {
	const [isLoading, setIsLoading] = React.useState(false);
	const [isBordereauCreation, setIsBordereauCreation] = React.useState(false);
	const [selectedBordereauId, setSelectedBordereauId] = React.useState<
		number | string | null
	>(
		mouvement?.codeBHM !== CODE_BHM.VALUE.HISTORISE
			? (mouvement?.identifiantBordereau ?? -1)
			: null,
	);
	const [errors, setErrors] = React.useState<string[]>([]);

	const [series, setSeries] = React.useState<DropdownOption[]>([]);
	const [loaded, setLoaded] = React.useState(false);
	const [customBordereau, setCustomBordereau] =
		React.useState<Pick<BordereauWithState, CustomBordereauKeys>>(null);
	const [job, setJob] = React.useState<Job | null>(null);

	const sens =
		(bordereau && bordereau.sens === Sens.RECETTE) ||
		(mouvement && mouvement.sens === Sens.RECETTE)
			? Sens.RECETTE
			: Sens.DEPENSE;

	const [formData, setFormData] = React.useState<FormData>({
		shouldReprendreReferences: true,
		bordereauIsModel: bordereau?.codeBHM === CODE_BHM.VALUE.MODELE,
		isEcheance: false,
		dateEcheance: null,
		isDepartDelaiGlobalPaiement: false,
		dateDepartDelaiGlobalPaiement: dayjs().toISOString(),
		isPrelevement: false,
		datePrelevement: null,
		isRealisationPrestation: false,
		dateRealisationPrestationDebut: dayjs().toISOString(),
		dateRealisationPrestationFin: null,
	});

	const handleChange = (
		key: keyof FormData,
		value?: string | boolean,
	): void => {
		setFormData((prevData) => ({
			...prevData,
			[key]: value,
		}));
	};

	const handleToggleAll = (status: boolean): void => {
		setFormData({
			...formData,
			isEcheance: status,
			isDepartDelaiGlobalPaiement: status,
			dateDepartDelaiGlobalPaiement: status
				? formData.dateDepartDelaiGlobalPaiement
				: null,
			isPrelevement: status,
			datePrelevement: status ? formData.datePrelevement : null,
			isRealisationPrestation: status,
			dateRealisationPrestationDebut: status
				? formData.dateRealisationPrestationDebut
				: null,
			dateRealisationPrestationFin: status
				? formData.dateRealisationPrestationFin
				: null,
		});
	};

	const isAllChecked =
		formData.isEcheance &&
		formData.isDepartDelaiGlobalPaiement &&
		formData.isPrelevement &&
		formData.isRealisationPrestation;

	const isAnyChecked =
		formData.isEcheance ||
		formData.isDepartDelaiGlobalPaiement ||
		formData.isPrelevement ||
		formData.isRealisationPrestation;

	const handleBordereauChange = (data: Bordereau) => {
		setSelectedBordereauId(data?.identifiant);
		setIsBordereauCreation(false);
	};

	const handleCreateBordereau = async (): Promise<Bordereau> => {
		setIsBordereauCreation(true);
		setSelectedBordereauId(null);
		return null as unknown as Bordereau;
	};

	const handleCheckRequiredFields = (): string[] => {
		const newErrors: string[] = [];

		if (sens === Sens.RECETTE) {
			if (formData.isPrelevement && isNil(formData.datePrelevement)) {
				newErrors.push(DUPLICATION_ERRORS.DUPLICATION_DATE_PRELEVEMENT);
			}

			if (
				formData.isRealisationPrestation &&
				isNil(formData.dateRealisationPrestationDebut)
			) {
				newErrors.push(DUPLICATION_ERRORS.DUPLICATION_REALISATION_PRESTATION);
			}
		} else {
			if (formData.isEcheance && isNil(formData.dateEcheance)) {
				newErrors.push(DUPLICATION_ERRORS.DUPLICATION_ECHEANCE);
			}

			if (
				formData.isDepartDelaiGlobalPaiement &&
				isNil(formData.dateDepartDelaiGlobalPaiement)
			) {
				newErrors.push(
					DUPLICATION_ERRORS.DUPLICATION_DEPART_DELAI_GLOBAL_PAIEMENT,
				);
			}
		}

		if (mouvement) {
			if (isBordereauCreation) {
				if (isNil(customBordereau?.dateEmission)) {
					newErrors.push(DUPLICATION_ERRORS.DUPLICATION_DATE_EMISSION);
				}
				if (isEmpty(customBordereau?.libelle)) {
					newErrors.push(DUPLICATION_ERRORS.DUPLICATION_BORDEREAU_LIBELLE);
				}
			} else if (isNil(selectedBordereauId)) {
				newErrors.push(DUPLICATION_ERRORS.DUPLICATION_BORDEREAU);
			}
		}

		return newErrors;
	};

	const handleJobError = () => {
		setJob(null);
		setIsLoading(false);
		toast.error('Erreur lors de la duplication du bordereau.');
	};

	const handleCloseJob = (finishedJob: Job) => {
		setJob(null);

		if (finishedJob.status === 1) {
			try {
				const response: any = JSON.parse(finishedJob.resultat);
				onValidate(response);
			} catch (error) {
				toast.error(
					getErrorMessage(
						error,
						'Une erreur est survenue lors de la duplication du bordereau.',
					),
				);
			} finally {
				setIsLoading(false);
				onClose();
			}
		} else {
			toast.error(finishedJob.resultat);
			setIsLoading(false);
		}
	};

	const handleValidate = async (): Promise<void> => {
		setIsLoading(true);

		const errorsFound = handleCheckRequiredFields();
		setErrors(errorsFound);
		if (errorsFound.length > 0) {
			setIsLoading(false);
			return;
		}

		const data = {
			...formData,
			dateEcheance: formData.isEcheance ? formData.dateEcheance : null,
			dateDepartDelaiGlobalPaiement: formData.isDepartDelaiGlobalPaiement
				? formData.dateDepartDelaiGlobalPaiement
				: null,

			dateRealisationPrestationDebut: formData.isRealisationPrestation
				? (formData.dateRealisationPrestationDebut ?? '') // différencier une date vide et une date non définie
				: null,
			dateRealisationPrestationFin: formData.isRealisationPrestation
				? formData.dateRealisationPrestationFin
				: null,
		};
		let onDuplicatePromise: null | Promise<any> = null;
		let onDuplicateJob: null | Job = null;

		if (bordereau) {
			// Traitement long
			const newBord = { ...bordereau, ...data };
			onDuplicateJob = await dupliquerBordereau(
				newBord,
				newBord.shouldReprendreReferences,
				newBord?.dateEcheance,
				newBord?.dateDepartDelaiGlobalPaiement,
				newBord?.datePrelevement,
				newBord?.dateRealisationPrestationDebut,
				newBord?.dateRealisationPrestationFin,
			);
			setJob(onDuplicateJob);
		} else if (mouvement) {
			const mouvementData = {
				...mouvement,
				...data,
			};

			if (isBordereauCreation) {
				onDuplicatePromise = onValidate({
					...mouvementData,
					identifiantBordereau: null,
					bordereau: {
						...customBordereau,
						codeBHM: mouvement.codeBHM,
						piecesCount: 0,
						retourPesKo: false,
						retourPesOk: false,
						alerteTraitee: false,
						acquitte: 0,
						edite: false,
					} as Bordereau,
				}) as Promise<any>;
			} else {
				onDuplicatePromise = onValidate({
					...mouvementData,
					bordereau: null,
					updatedIdBordereau: selectedBordereauId as number,
				} as ChangeDataMouvement) as Promise<any>;
			}

			onDuplicatePromise.finally(() => {
				toast.success('Duplication effectuée avec succès');
				setIsLoading(false);
				onClose();
			});
		}
	};

	React.useEffect(() => {
		if (!isNil(exercice?.identifiant) && !loaded) {
			getSeries(
				new URLSearchParams({
					identifiantExercice: exercice?.identifiant?.toString(),
				}),
			)
				.then(({ data }) => {
					setSeries([
						HORS_SERIE,
						...map(data, (serie) => ({
							'data-testid': `serie${serie.identifiant}`,
							key: serie?.identifiant,
							text: serie?.libelle,
							value: serie?.identifiant,
						})),
					]);
					setLoaded(true);
				})
				.catch(() => {
					toast.error('Impossible de récupérer les séries');
				});
		}
	}, [exercice, loaded]);

	return (
		<Modal
			closeIcon
			open={visible}
			onClose={onClose}
			data-testid="duplicationModal"
		>
			<Modal.Header>
				Dupliquer le {bordereau ? 'bordereau' : 'mouvement'} n°
				{bordereau ? bordereau.numero : mouvement.numero}
			</Modal.Header>
			<Modal.Content>
				{errors.length > 0 && (
					<Message
						error
						header="Votre saisie comporte des erreurs:"
						list={errors}
					/>
				)}
				<p>
					{bordereau &&
						(sens === Sens.DEPENSE
							? 'Cette action permet de dupliquer ce bordereau et tous ses mandats pour constituer un nouveau bordereau en-cours.'
							: 'Cette action permet de dupliquer ce bordereau et tous ses titres pour constituer un nouveau bordereau en-cours.')}
					{mouvement &&
						(sens === Sens.DEPENSE
							? 'Cette action permet de dupliquer ce mandat dans un bordereau de dépense en-cours.'
							: 'Cette action permet de dupliquer ce titre dans un bordereau de recette en-cours.')}
				</p>
				<Form>
					<Form.Checkbox
						label="Reprendre les références des pièces justificatives"
						checked={formData.shouldReprendreReferences}
						onChange={() =>
							handleChange(
								'shouldReprendreReferences',
								!formData.shouldReprendreReferences,
							)
						}
						data-testid="reprendreReferencesCheckbox"
					/>
					<Form.Checkbox
						label={`Réinitialiser les dates suivantes lorsque les champs correspondants sont renseignés sur les 
              ${sens === Sens.DEPENSE ? 'mandats' : 'titres'}`}
						checked={isAllChecked}
						indeterminate={isAnyChecked && !isAllChecked}
						onChange={() => handleToggleAll(!isAllChecked)}
						data-testid="reinitialiserDatesCheckbox"
					/>
					{sens === Sens.DEPENSE && (
						<>
							<Form.Group inline>
								<Form.Field width={1} />
								<Form.Checkbox
									label="Échéance"
									checked={formData.isEcheance}
									onChange={() =>
										handleChange('isEcheance', !formData.isEcheance)
									}
									data-testid="echeanceCheckbox"
									width={5}
								/>
								{formData.isEcheance ? (
									<Form.Field
										control={Date}
										minDate={dayjs().startOf('day').add(1, 'day')}
										onChange={(value) => handleChange('dateEcheance', value)}
										value={formData.dateEcheance}
										disabled={!formData.isEcheance}
										data-testid="echeanceInput"
										width={4}
										fluid
									/>
								) : (
									<Form.Field width={4} />
								)}
							</Form.Group>
							<Form.Group inline>
								<Form.Field width={1} />
								<Form.Checkbox
									label="Départ du délai global de paiement"
									checked={formData.isDepartDelaiGlobalPaiement}
									onChange={() =>
										handleChange(
											'isDepartDelaiGlobalPaiement',
											!formData.isDepartDelaiGlobalPaiement,
										)
									}
									data-testid="departDelaiCheckbox"
									width={5}
								/>
								{formData.isDepartDelaiGlobalPaiement ? (
									<Form.Field
										control={Date}
										onChange={(value) =>
											handleChange('dateDepartDelaiGlobalPaiement', value)
										}
										value={formData.dateDepartDelaiGlobalPaiement}
										disabled={!formData.isDepartDelaiGlobalPaiement}
										data-testid="departDelaiInput"
										width={4}
										fluid
									/>
								) : (
									<Form.Field width={4} />
								)}
							</Form.Group>
						</>
					)}

					{sens === Sens.RECETTE && (
						<>
							<Form.Group inline>
								<Form.Field width={1} />
								<Form.Checkbox
									label="Prélèvement"
									checked={formData.isPrelevement}
									onChange={() =>
										handleChange('isPrelevement', !formData.isPrelevement)
									}
									data-testid="prelevementCheckbox"
									width={5}
								/>
								{formData.isPrelevement ? (
									<Form.Field
										control={Date}
										onChange={(value) => handleChange('datePrelevement', value)}
										value={formData.datePrelevement}
										disabled={!formData.isPrelevement}
										data-testid="prelevementInput"
										width={4}
										fluid
									/>
								) : (
									<Form.Field width={4} />
								)}
							</Form.Group>
							<Form.Group inline>
								<Form.Field width={1} />
								<Form.Checkbox
									label="Réalisation de la prestation"
									checked={formData.isRealisationPrestation}
									onChange={() =>
										handleChange(
											'isRealisationPrestation',
											!formData.isRealisationPrestation,
										)
									}
									data-testid="realisationCheckbox"
									width={5}
								/>
								{formData.isRealisationPrestation ? (
									<>
										<Form.Field
											control={Date}
											onChange={(value) =>
												handleChange('dateRealisationPrestationDebut', value)
											}
											value={formData.dateRealisationPrestationDebut}
											disabled={!formData.isRealisationPrestation}
											data-testid="realisationInput"
											width={4}
											fluid
											placeholder="Date de début"
										/>
										<Form.Field
											control={Date}
											onChange={(value) =>
												handleChange('dateRealisationPrestationFin', value)
											}
											value={formData.dateRealisationPrestationFin}
											disabled={!formData.isRealisationPrestation}
											data-testid="realisationFinInput"
											width={4}
											fluid
											placeholder="Date de fin"
										/>
									</>
								) : (
									<Form.Field width={4} />
								)}
							</Form.Group>
						</>
					)}
					{!isNil(mouvement) && (
						<div style={{ marginTop: '3em' }}>
							<Form.Group>
								<BordereauSelect
									value={selectedBordereauId}
									required
									identifiant="new"
									mouvement={mouvement}
									onChange={handleBordereauChange}
									onCreateBordereau={handleCreateBordereau}
									label="Bordereau de destination"
									width={16}
									data-testid="bordereauDestination"
								/>
							</Form.Group>
							{isBordereauCreation && (
								<>
									<Header as="h4"> Création d&apos;un nouveau bordereau</Header>
									<BordereauForm
										bordereau={{
											sens: mouvement?.sens,
											codeBHM: mouvement?.codeBHM,
											typePes,
										}}
										customBordereau={customBordereau}
										setCustomBordereau={setCustomBordereau}
										series={series}
										numeroDisabled
										typeDisabled
										sensDisabled
									/>
								</>
							)}
						</div>
					)}
				</Form>
				{isLoading && job && (
					<TraitementLong
						title="Duplication du bordereau"
						identifiantJob={job.identifiant}
						onFinish={handleCloseJob}
						onError={handleJobError}
					/>
				)}
			</Modal.Content>
			<Modal.Actions>
				<Button onClick={onClose} data-testid="cancelDuplicationModalBtn">
					Annuler
				</Button>
				<Button
					primary
					onClick={handleValidate}
					loading={isLoading}
					data-testid="confirmDuplicationModalBtn"
				>
					Confirmer
				</Button>
			</Modal.Actions>
		</Modal>
	);
};

export default DuplicationModal;
